import compact from 'lodash/compact';
import { useMemo } from 'react';

import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import useInfiniteService from '@/data/hook/useInfiniteService';
import {
  lessonBlocksQueryKeys,
  scheduledLessonsQueryKeys,
} from '@/data/services/querykeys';
import Lesson from '@/models/Lesson';
import ScheduledLesson from '@/models/ScheduledLesson';
import { CourseRoute } from '@/utils/routes';

export type StaffUnitLessonEntry = {
  id: number;
  lesson: Lesson;
  scheduledLesson?: ScheduledLesson;
};

type UseStaffUnitLessonsParams = CourseRoute & {
  unitId: number;
};

export default function useStaffUnitLessons({
  unitId,
  slug,
  klassId,
}: UseStaffUnitLessonsParams) {
  const hasKlass = !!klassId;

  const scheduledLessons = useInfiniteService({
    ...scheduledLessonsQueryKeys.list({
      klassId: Number(klassId),
      lessonBlock: [unitId],
    })._ctx.infinity,
    enabled: hasKlass,
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });

  const courseLessons = useInfiniteService({
    ...lessonBlocksQueryKeys.listLessons(slug, unitId)._ctx.infinity,
    enabled: !hasKlass,
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });

  const { results: scheduledResults } = scheduledLessons;
  const { results: courseResults } = courseLessons;

  const entries = useMemo<StaffUnitLessonEntry[]>(
    () =>
      hasKlass
        ? compact(
            scheduledResults.map(
              scheduledLesson =>
                scheduledLesson.lesson && {
                  id: scheduledLesson.id,
                  lesson: scheduledLesson.lesson,
                  scheduledLesson,
                },
            ),
          )
        : courseResults.map(lesson => ({ id: lesson.id, lesson })),
    [hasKlass, scheduledResults, courseResults],
  );

  const {
    isInitialLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    meta,
  } = hasKlass ? scheduledLessons : courseLessons;

  return {
    entries,
    totalLessons: meta?.pagination.count,
    isInitialLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  };
}
